import { Box, Stack, Typography } from "@mui/material";
import type { ConsultationDecryptedDetail } from "./consultations";

export interface ConsultationSignaturesProps {
  detail: ConsultationDecryptedDetail | null;
}

const PNG_DATA_URL = /^data:image\/png;base64,[A-Za-z0-9+/=]+$/;

function isRenderableSignature(value: unknown): value is string {
  return typeof value === "string" && PNG_DATA_URL.test(value.trim());
}

function SignatureRow({ label, value }: { label: string; value: unknown }) {
  const src = isRenderableSignature(value) ? String(value).trim() : null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      {src ? (
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{
            display: "block",
            maxWidth: 260,
            maxHeight: 90,
            width: "auto",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            p: 0.5,
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Not signed
        </Typography>
      )}
    </Box>
  );
}

/**
 * Task #625 — Signatures section shared by Chain Admin and Pharmacy Admin
 * consultation detail pages. Signature resolution mirrors the mobile PDF
 * generator: the `signatures` block wins, with the form-data fields as a
 * fallback. Anything that is not a well-formed PNG data URL renders as
 * "Not signed" rather than a broken image.
 */
export function ConsultationSignatures({ detail }: ConsultationSignaturesProps) {
  const fd = detail?.formData ?? {};
  const sigs = detail?.signatures;
  const patient = sigs?.patientSignature || fd["patient_signature"] || null;
  const guardian = sigs?.guardianSignature || fd["guardian_signature"] || null;
  const pharmacist = fd["pharmacist_signature"] || null;
  const showGuardian =
    !!sigs?.patientIsUnder16 ||
    String(fd["patient_is_under_16"] || "").toLowerCase() === "yes" ||
    isRenderableSignature(guardian);

  return (
    <Stack spacing={1.5}>
      <SignatureRow label="Patient signature" value={patient} />
      {showGuardian ? (
        <SignatureRow label="Parent / guardian signature" value={guardian} />
      ) : null}
      <SignatureRow label="Pharmacist signature" value={pharmacist} />
      {sigs?.signedAt ? (
        <Typography variant="caption" color="text.secondary">
          Signed: {new Date(sigs.signedAt).toLocaleString()}
        </Typography>
      ) : null}
    </Stack>
  );
}
